'use client';
import React, { useState } from 'react';
import { Plus, Check, X, Edit, Upload, Download } from 'lucide-react';

export default function LanguagesManagers() {
  const [languages, setLanguages] = useState([
    {
      name: 'English',
      flag: '🇺🇸',
      culture: 'en-US',
      order: 1,
      published: true,
    },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [newLanguage, setNewLanguage] = useState({
    name: '',
    flag: '',
    culture: '',
    order: '',
    published: true,
  });

  const handleAddLanguage = () => {
    if (!newLanguage.name || !newLanguage.culture) return;
    setLanguages([...languages, newLanguage]);
    setNewLanguage({ name: '', flag: '', culture: '', order: '', published: true });
    setShowModal(false);
  };

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto bg-white shadow-md rounded-lg p-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
          <h2 className="text-2xl font-semibold text-gray-800">Languages</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-3 py-2 rounded flex items-center hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Add new
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Learn more about{' '}
          <a href="#" className="text-blue-600 underline">
            localization
          </a>
        </p>

        {/* Table */}
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full border-collapse text-sm text-gray-700">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 border-b text-left">Name</th>
                <th className="p-3 border-b text-left">Flag</th>
                <th className="p-3 border-b text-left">Language culture</th>
                <th className="p-3 border-b text-left">Display order</th>
                <th className="p-3 border-b text-left">Published</th>
                <th className="p-3 border-b text-left">Edit</th>
              </tr>
            </thead>
            <tbody>
              {languages.map((lang, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-3 border-b">{lang.name}</td>
                  <td className="p-3 border-b text-center">{lang.flag || '—'}</td>
                  <td className="p-3 border-b">{lang.culture}</td>
                  <td className="p-3 border-b text-center">{lang.order || '-'}</td>
                  <td className="p-3 border-b text-center">
                    {lang.published ? (
                      <Check className="text-blue-600 w-4 h-4 inline" />
                    ) : (
                      <X className="text-red-500 w-4 h-4 inline" />
                    )}
                  </td>
                  <td className="p-3 border-b">
                    <button className="bg-gray-100 px-3 py-1 rounded hover:bg-gray-200 flex items-center">
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap justify-between items-center mt-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 border rounded hover:bg-gray-100">Previous</button>
            <button className="px-3 py-1 border rounded bg-blue-50 text-blue-700">1</button>
            <button className="px-3 py-1 border rounded hover:bg-gray-100">Next</button>
          </div>

          <div className="flex items-center gap-2">
            <label>Show</label>
            <select className="border rounded px-2 py-1">
              <option>15</option>
              <option>30</option>
              <option>50</option>
            </select>
            <span>items</span>
          </div>
          <span className="text-gray-500">1–{languages.length} of {languages.length} items</span>
        </div>
      </div>

      {/* Modal for Adding New Language */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-xl font-semibold mb-4">Add New Language</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={newLanguage.name}
                  onChange={(e) => setNewLanguage({ ...newLanguage, name: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Flag (Emoji or URL)</label>
                <input
                  type="text"
                  value={newLanguage.flag}
                  onChange={(e) => setNewLanguage({ ...newLanguage, flag: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Language culture</label>
                <input
                  type="text"
                  value={newLanguage.culture}
                  onChange={(e) => setNewLanguage({ ...newLanguage, culture: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Display order</label>
                <input
                  type="number"
                  value={newLanguage.order}
                  onChange={(e) => setNewLanguage({ ...newLanguage, order: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newLanguage.published}
                  onChange={(e) => setNewLanguage({ ...newLanguage, published: e.target.checked })}
                  className="mr-2"
                />
                <label>Published</label>
              </div>
            </div>

            <div className="flex justify-end mt-4 gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLanguage}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
